import { useEffect, useMemo, useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useSearchParams } from 'react-router-dom'
import BaseUITabsComponent from 'src/common/tabs/BaseUITabs'
import SupportMentorsListing from 'src/supportPortal/components/common/mentor/listing/SupportMentorsListing'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const TabsContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(99, 102, 241, 0.12) 100%)',
  padding: theme.spacing(2),
  borderRadius: '0 0 16px 16px',
  borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: '0 0 12px 12px',
  },
}))

function normalizeTargetSpecialties(resp) {
  if (!resp || resp.status !== 200) return []
  let list = []
  if (Array.isArray(resp.data)) {
    list = resp.data
  } else if (isCommonResponseSuccessfulV2(resp)) {
    const data = getDataMapFromCommonResponse(resp)
    if (Array.isArray(data)) list = data
    else if (data?.content && Array.isArray(data.content)) list = data.content
    else if (data?.targetSpecialties && Array.isArray(data.targetSpecialties)) list = data.targetSpecialties
    else if (data && typeof data === 'object') {
      const key = Object.keys(data).find((k) => Array.isArray(data[k]))
      if (key) list = data[key]
    }
  }
  if (list.length === 0 && resp.data?.content) list = resp.data.content || []
  if (list.length === 0 && resp.data?.data) list = Array.isArray(resp.data.data) ? resp.data.data : []
  return Array.isArray(list) ? list : []
}

/**
 * Standalone Screeners page (lifted out of the Therapists listing). Tabs are derived from the
 * target-specialties config; "No specialty" catches screeners not tagged to a specialty. The
 * selected tab is persisted in the URL so the page is bookmarkable.
 */
export default function ScreenersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [targetSpecialties, setTargetSpecialties] = useState([])

  useEffect(() => {
    let cancelled = false
    async function fetchTargetSpecialties() {
      const resp = await getSPDataWithParams('/api/secure/cc/support/target-specialties/page', {
        includeDisabled: false,
        page: 0,
        size: 200,
      })
      if (cancelled) return
      setTargetSpecialties(normalizeTargetSpecialties(resp))
    }
    fetchTargetSpecialties()
    return () => {
      cancelled = true
    }
  }, [])

  const tabs = useMemo(() => {
    const specialtyTabs = (targetSpecialties || []).map((s) => ({
      id: `specialty-${s.slug || s.id || s.title}`,
      label: s.title || s.name || s.slug || 'Unknown',
      slug: s.slug || s.id,
    }))
    const noSpecialty = { id: 'no-specialty', label: 'No specialty', slug: null }
    return specialtyTabs.length > 0 ? [...specialtyTabs, noSpecialty] : [noSpecialty]
  }, [targetSpecialties])

  const initialTab = searchParams.get('tab') || tabs[0]?.id || 'no-specialty'
  const [selectedTab, setSelectedTab] = useState(initialTab)

  // If the URL has no tab and the first specialty tab has loaded, sync state to it.
  useEffect(() => {
    if (!searchParams.get('tab') && tabs[0]?.id && selectedTab !== tabs[0].id) {
      setSelectedTab(tabs[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs])

  function handleTabChange(nextId) {
    setSelectedTab(nextId)
    const next = new URLSearchParams(searchParams)
    next.set('tab', nextId)
    setSearchParams(next, { replace: true })
  }

  const isNoSpecialty = selectedTab === 'no-specialty'
  const screenerSpecialtySlug = useMemo(() => {
    if (isNoSpecialty) return null
    const tab = tabs.find((t) => t.id === selectedTab)
    return tab?.slug
  }, [selectedTab, isNoSpecialty, tabs])

  const listApiParams = !isNoSpecialty && screenerSpecialtySlug ? { speciality: screenerSpecialtySlug } : {}

  return (
    <>
      <SupportHeader heading="Screeners" showBackButton />
      <TabsContainer>
        <Box sx={{ maxWidth: 1400, margin: '0 auto' }}>
          <BaseUITabsComponent
            variant="contained"
            tabs={tabs}
            value={selectedTab}
            unselectedColor="rgba(71, 85, 105, 0.85)"
            selectedColor="rgba(99, 102, 241, 0.95)"
            hoverItemColor="rgba(99, 102, 241, 0.9)"
            onChange={handleTabChange}
            getTabId={(tab) => tab.id}
            getTabLabel={(tab) => tab.label}
            incicatorColor="primary"
          />
        </Box>
      </TabsContainer>
      <SupportMentorsListing
        mentorsListApiSignature="/api/secure/cc/support/screeners/clinical/list"
        listApiParams={listApiParams}
        variant="screener"
        heading="Screeners"
        exculudeMenuItemsList={[]}
        hideHeading
        screenerNoSpecialtyFilter={isNoSpecialty}
        showOnboardingButton
        onboardingPractitionerType="screener"
      />
    </>
  )
}
